import { useEffect, useState } from 'react';
import {
  useGetEpisodesForSeasonQuery,
  useGetSeasonsForSeriesQuery,
} from '#types/generated/apollo-client';
import SeasonSwitch from '#modules/series-details/SeasonsSwitch';
import {
  Media,
  ProductAccessReason,
  Season,
} from '#types/generated/graphql-types';
import useViewport from '#hooks/useViewport';
import { useOvermindActions, useOvermindState } from '#store/overmind';
import EpisodeItemSmall from '#modules/series-details/EpisodeItemSmall';
import EpisodeItemLarge from '#modules/series-details/EpisodeItemLarge';
import { useLoginNotificationDialog } from '#modules/dialog-login-notification/DialogLoginNotification';
import { useRentNotificationDialog } from '#modules/dialog-rent-notification/DialogRentNotification';
import PageSection from '#components/sections/page-section/PageSection';
import CircularProgress from '#components/feedback/circular-progress/CircularProgress';
import { triggerPlayerPlay } from '#utils/player/controls';

type Props = {
  seriesId: number;
};

const EpisodeExplorer = ({ seriesId }: Props) => {
  const { initPlayer } = useOvermindActions().player;
  const { itemMap } = useOvermindState().continueWatching;
  const { isSmall } = useViewport();
  const { showLoginNotificationDialog } = useLoginNotificationDialog();
  const { showRentNotificationDialog } = useRentNotificationDialog();
  const {
    user: { profile },
  } = useOvermindState();
  const [selectedSeason, setSelectedSeason] = useState<Season | undefined>();

  const { data: series, loading: seasonsLoading } = useGetSeasonsForSeriesQuery(
    {
      ssr: false,
      variables: {
        seriesId,
      },
    }
  );

  const { data: season, loading: episodesLoading } =
    useGetEpisodesForSeasonQuery({
      ssr: false,
      variables: {
        seasonId: selectedSeason?.id,
      },
      skip: !selectedSeason,
    });

  useEffect(() => {
    if (series?.seriesById?.seasons) {
      setSelectedSeason(series.seriesById.seasons[0]);
    }
  }, [series]);

  const handleSeasonChange = (seasonId) => {
    const newSelectedSeason = series?.seriesById?.seasons.find(
      (item) => item.id === seasonId
    );
    setSelectedSeason(newSelectedSeason);
  };

  const handlePlayButtonClick = (item: Media) => {
    //todo - use common logic for triggering play button click @see HeroButtonParade | SeriesHeroButtonParade - marius.iliescu

    const access = item?.products?.access;

    if (
      access?.allowed === false &&
      access?.reason === ProductAccessReason.LoginRequired &&
      !profile
    ) {
      showLoginNotificationDialog();
      return;
    }

    initPlayer({ iri: item.iri, contentType: item.contentType });
    triggerPlayerPlay();
  };

  const handleProductButtonClick = (mediaIri, productIri) => {
    if (!profile) {
      showLoginNotificationDialog('purchase');
      return;
    }

    showRentNotificationDialog(mediaIri, productIri);
  };

  if (seasonsLoading || !series || !selectedSeason) {
    return (
      <PageSection>
        <div className="tw-flex tw-items-center tw-justify-center">
          <CircularProgress />
        </div>
      </PageSection>
    );
  }

  return (
    <div>
      <SeasonSwitch
        classes="tw-mb-8"
        items={series?.seriesById?.seasons}
        selectedId={selectedSeason.id}
        onChange={handleSeasonChange}
        hideSeasonNumbers={series?.seriesById?.hideSeasonNumbers}
      />
      {(episodesLoading || !season) && (
        <PageSection>
          <div className="tw-flex tw-items-center tw-justify-center">
            <CircularProgress />
          </div>
        </PageSection>
      )}
      <div>
        {season?.seasonById?.episodes?.map((item) => {
          const Component = isSmall ? EpisodeItemSmall : EpisodeItemLarge;
          return (
            <Component
              continueWatchingState={
                !!profile && itemMap[item.iri] ? itemMap[item.iri] : null
              }
              key={item.id}
              item={item}
              onPlayButtonClick={handlePlayButtonClick}
              onProductButtonClick={handleProductButtonClick}
            />
          );
        })}
      </div>
    </div>
  );
};

export default EpisodeExplorer;
